import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { AuthContext } from '../App';
import Footer from './Footer';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx'; // Import xlsx library for Excel export
import { saveAs } from 'file-saver'; // For downloading the file
import { ArrowClockwise, Trash } from 'react-bootstrap-icons';
function RegisteredUsers(props) {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [countries, setCountries] = useState(null);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedRole, setSelectedRole] = useState('extoff');
    const [formError, setFormError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [activeTab, setActiveTab] = useState(null);
    const userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
    const isSuperAdmin = +userDetails?.role_id === 1;
    useEffect(() => {
        if (selectedCountry !== null) {
            handleFilterSubmit();
        }
    }, [selectedCountry]);
    useEffect(() => {
        if (countries) {
            if (!isSuperAdmin) {
                setSelectedCountry(userDetails.allowed_country_id || null);
            } else {
                setSelectedCountry(countries[1]?.country_id || null);
            }
            handleFilterSubmit();
        }
    }, [countries]);
    const reset = () => {
        setSearchTerm('');
        setPageNo(1);
        setSelectedRows([]);
        setSelectedRole('extoff');
        fetchUsers('');
    };
    const handleDelete = async (user) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete ${user.first_name}${user.last_name ? ' ' + user.last_name : ''}? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            const payload = {
                country_id: user.country_id,
                registered_as: user.registered_as,
                staging_id: user.fisher_id,
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/self_registration`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Deleted!',
                    text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} has been deleted.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                const newTotalRows = totalRows - 1;
                const recordsOnCurrentPage = newTotalRows - (pageNo - 1) * perPage;
                if (recordsOnCurrentPage <= 0 && pageNo > 1) {
                    setPageNo(pageNo - 1);
                } else if (newTotalRows === 0) {
                    setPageNo(1);
                }
                setTotalRows(newTotalRows);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to delete the user.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    const handleApprove = async (user) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to approve ${user.first_name}${user.last_name ? ' ' + user.last_name : ''}?`,
            icon: 'warning',
            input: 'text',
            inputLabel: 'Remarks (optional)',
            inputPlaceholder: 'Enter remarks',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, approve!',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            const payload = {
                country_id: user.country_id,
                registered_as: user.registered_as,
                staging_id: user.fisher_id,
                registration_status: "approve",
                remarks: result.value || '',
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/self_registration`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Approved!',
                    text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} has been approved.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                const newTotalRows = totalRows - 1;
                const recordsOnCurrentPage = newTotalRows - (pageNo - 1) * perPage;
                if (recordsOnCurrentPage <= 0 && pageNo > 1) {
                    setPageNo(pageNo - 1);
                } else if (newTotalRows === 0) {
                    setPageNo(1);
                }
                setTotalRows(newTotalRows);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to approve the user.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    const handleReject = async (user) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to reject ${user.first_name}${user.last_name ? ' ' + user.last_name : ''}?`,
            icon: 'warning',
            input: 'text',
            inputLabel: 'Remarks (optional)',
            inputPlaceholder: 'Enter remarks',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, reject!',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            const payload = {
                country_id: user.country_id,
                registered_as: user.registered_as,
                staging_id: user.fisher_id,
                registration_status: "reject",
                remarks: result.value || '',
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/self_registration`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Rejected!',
                    text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} has been rejected.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                const newTotalRows = totalRows - 1;
                const recordsOnCurrentPage = newTotalRows - (pageNo - 1) * perPage;
                if (recordsOnCurrentPage <= 0 && pageNo > 1) {
                    setPageNo(pageNo - 1);
                } else if (newTotalRows === 0) {
                    setPageNo(1);
                }
                setTotalRows(newTotalRows);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to reject the user.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    const handleActivate = async (user) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to make this user active?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, activate!',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            const payload = {
                country_id: user.country_id,
                registered_as: user.registered_as,
                staging_id: user.fisher_id,
                purpose: 'reactivate',
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/self_registration`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Activated!',
                    text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} has been activated.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                const newTotalRows = totalRows - 1;
                const recordsOnCurrentPage = newTotalRows - (pageNo - 1) * perPage;
                if (recordsOnCurrentPage <= 0 && pageNo > 1) {
                    setPageNo(pageNo - 1);
                } else if (newTotalRows === 0) {
                    setPageNo(1);
                }
                setTotalRows(newTotalRows);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to activate the user.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    const handleReset = async (user) => {
        const currentYear = new Date().getFullYear();
        const defaultPassword = `bLuEResiLience@${currentYear}`;
        const result = await Swal.fire({
            title: 'Are you sure?',
            html: `The User's password be reset to <br><strong>${defaultPassword}</strong>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reset it!',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            const payload = {
                purpose: 'reset_password',
                user_id: user.registered_user_id
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/user_mgmt`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Password Reset!',
                    html: `Password for ${user.first_name}${user.last_name ? ' ' + user.last_name : ''} has been reset.<br>New Password: <strong>${defaultPassword}</strong>`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to reset password.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    const handleBulkApprove = async () => {
        if (selectedRows.length === 0) return;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to approve ${selectedRows.length} users?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, approve!',
        });
        if (result.isConfirmed) {
            const staging_ids = selectedRows.map(row => row.fisher_id);
            const payload = {
                registered_as: selectedRole,
                country_id: Number(selectedCountry),
                staging_ids,
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/self_registration/bulk/approve`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Approved!',
                    text: `${selectedRows.length} users have been approved.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                setSelectedRows([]);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to approve the users.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    const handleBulkReject = async () => {
        if (selectedRows.length === 0) return;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to reject ${selectedRows.length} users?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, reject!',
        });
        if (result.isConfirmed) {
            const staging_ids = selectedRows.map(row => row.fisher_id);
            const payload = {
                registered_as: selectedRole,
                country_id: Number(selectedCountry),
                staging_ids,
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/self_registration/bulk/reject`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                await Swal.fire({
                    title: 'Rejected!',
                    text: `${selectedRows.length} users have been rejected.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
                setSelectedRows([]);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to reject the users.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    useEffect(() => {
        if (!isAuthenticated) {
            window.location.href = '/login';
        }
    }, [isAuthenticated, navigate]);
    const getAuthHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');
        const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);
        if (isTokenExpired) {
            logout();
            window.location.href = '/login';
            return {};
        }
        return {
            'Content-Type': 'application/json',
            Authorization: token,
        };
    };
    const handleApiError = (error, response) => {
        if (
            response &&
            response.status === 401 &&
            error.message.includes('Your authentication token is invalid')
        ) {
            setTimeout(() => {
                logout();
                window.location.href = '/login';
            }, 0);
            return 'Session expired. Please log in again.';
        }
        return error.message || 'An error occurred. Please try again.';
    };
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/locations/country`, {
                    headers: getAuthHeaders(),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setCountries(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };
        if (isAuthenticated) {
            fetchCountries();
            setRoles([
                { role_id: 'farmer', role: 'Fish Farmer' },
                { role_id: 'extoff', role: 'Extension Officer' }
            ]);
        }
    }, [isAuthenticated]);
    const fetchUsers = async (term = searchTerm, isExport = false) => {
        if (!selectedCountry) {
            setFetchError('Please choose a country');
            setIsLoading(false);
            return;
        }
        if (!selectedRole) {
            setFetchError('Please choose the registration type');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setFetchError(null);
        if (!activeTab) {
            setActiveTab('submitted');
        }
        try {
            let url = `${envConfig.API_URL}/auth/self_registration?country_id=${selectedCountry || ''}`;
            if (!isExport) {
                url += `&page=${pageNo}&size=${perPage}`;
            }
            let status = activeTab === 'deleted' ? 'Deleted' : 'active';
            url += `&status=${status}`;
            if (activeTab !== 'deleted') {
                url += `&registration_status=${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`;
            }
            url += `&registered_as=${selectedRole}`;
            if (term) url += `&search_for=${encodeURIComponent(term)}`;
            const response = await fetch(url, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }
            const data = await response.json();
            if (data.success === 1) {
                const userData = data.data.map(u => ({
                    ...u,
                    fisher_id: u.fisher_id,
                    first_name: u.name.split(' ')[0],
                    last_name: u.name.split(' ').slice(1).join(' ') || '',
                    country_id: u.country_id,
                    allowed_country: countries?.find(c => c.country_id === u.country_id)?.country || '--',
                    registered_as: selectedRole,
                    role: roles.find(r => r.role_id === selectedRole)?.role || (selectedRole ? selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1) : '--'),
                    registration_status: u.registration_status || (activeTab !== 'deleted' ? activeTab.charAt(0).toUpperCase() + activeTab.slice(1) : null),
                    status: u.active ? 'Active' : 'Deleted',
                    registered_user_id: u.registered_user_id || null,
                })) || [];
                if (isExport) {
                    return userData; // Return data for export
                }
                setUsers(userData);
                const newTotalRows = data.metadata?.total_count || userData.length;
                setTotalRows(newTotalRows);
            } else {
                throw new Error('API request failed');
            }
        } catch (error) {
            setFetchError(handleApiError(error, error.response));
            setUsers([]);
            setTotalRows(0);
        } finally {
            setIsLoading(false);
        }
    };
    const handleFilterSubmit = () => {
        if (!selectedCountry) {
            setFormError('Country selection is mandatory.');
            return;
        }
        setFormError(null);
        setPageNo(1);
        fetchUsers();
    };
    const handleSearchSubmit = (e) => {
        if (e) e.preventDefault();
        if (!selectedCountry) {
            setFormError('Please select a country before searching.');
            return;
        }
        setPageNo(1);
        setSelectedRows([]);
        fetchUsers();
    };
    const onCountryChange = (e) => {
        setSelectedCountry(e.target.value);
    };
    const onRoleChange = (e) => {
        setSelectedRole(e.target.value);
    };
    // New function to handle Excel export
    const handleExport = async () => {
        try {
            setIsLoading(true);
            const exportData = await fetchUsers(searchTerm, true); // Fetch all data without pagination
            if (!exportData || exportData.length === 0) {
                Swal.fire({
                    title: 'No Data',
                    text: 'No users found to export.',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }
            const adm3Label = selectedCountry == 1 ? 'EPA' : 'Camp';
            // Map data to match DataTable columns
            const exportFormattedData = exportData.map((row, index) => ({
                SNo: index + 1,
                Country: row.allowed_country || '--',
                'First Name': row.first_name || '--',
                'Last Name': row.last_name || '--',
                Email: row.email || '--',
                Phone: row.phone || '--',
                Role: row.role || '--',
                Language: row.language || '--',
                Species: row.species || '--',
                'Advisory Preference': row.advisory_preference || '--',
                'Created On': row.created_on || '--',
                'Verified On': row.verified_on || '--',
                'Verified By': row.verified_by_user || '--',
                Province: row.adm1 || '--',
                District: row.adm2 || '--',
                [adm3Label]: row.adm3 || '--',
                Remarks: row.remarks || '--',
                Active: row.active ? 'Yes' : 'No',
                'User Status': row.as_user_status || '--',
                'Recipient Status': row.as_recipient_status || '--',

                Status: row.registration_status || 'Active',
            }));
            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportFormattedData);
            // Define column widths for better readability
            ws['!cols'] = [
                { wch: 10 }, // SNo
                { wch: 20 }, // Country
                { wch: 20 }, // First Name
                { wch: 20 }, // Last Name
                { wch: 30 }, // Email
                { wch: 20 }, // Phone
                { wch: 15 }, // Role
                { wch: 20 }, // Language
                { wch: 30 }, // Species
                { wch: 20 }, // Advisory Preference
                { wch: 20 }, // Created On
                { wch: 20 }, // Verified On
                { wch: 20 }, // Verified By
                { wch: 15 }, // Province
                { wch: 15 }, // District
                { wch: 15 }, // adm3Label
                { wch: 30 }, // Remarks
                { wch: 10 }, // Active
                { wch: 15 }, // Status
                ,
                { wch: 15 }, // User Status
                { wch: 20 }, // Recipient Status
            ];
            // Add headers styling (optional, requires additional setup)
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Users');
            // Generate Excel file
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
            saveAs(data, `Users_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to export data.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setIsLoading(false);
        }
    };
    const adm3Label = selectedCountry == 1 ? 'EPA' : 'Camp';
    const baseColumns = [
        {
            name: 'SNo',
            selector: (row, index) => {
                const sno = index + 1 + (pageNo - 1) * perPage;
                return sno;
            },
            sortable: false,
            width: '60px',
        },
        {
            name: 'Country',
            selector: (row) => row.allowed_country || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: 'First Name',
            selector: (row) => row.first_name || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Last Name',
            selector: (row) => row.last_name || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Email',
            selector: (row) => row.email || '--',
            sortable: true,
            width: '250px',
        },
        {
            name: 'Phone',
            selector: (row) => row.phone || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Role',
            selector: (row) => row.role || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Language',
            selector: (row) => row.language || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Species',
            selector: (row) => row.species || '--',
            sortable: true,
            width: '250px',
        },
        {
            name: 'Advisory Preference',
            selector: (row) => row.advisory_preference || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Created On',
            selector: (row) => row.created_on || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Verified On',
            selector: (row) => row.verified_on || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Verified By',
            selector: (row) => row.verified_by_user || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Province',
            selector: (row) => row.adm1 || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'District',
            selector: (row) => row.adm2 || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: adm3Label,
            selector: (row) => row.adm3 || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Remarks',
            selector: (row) => row.remarks || '--',
            sortable: true,
            width: '300px',
        },
        {
            name: 'Active',
            selector: (row) => row.active ? 'Yes' : 'No',
            sortable: true,
            width: '100px',
        },
        {
            name: 'User Status',
            selector: (row) => row.as_user_status || '--',
            sortable: true,
            width: '120px',
        },
        {
            name: 'Recipient Status',
            selector: (row) => row.as_recipient_status || '--',
            sortable: true,
            width: '150px',
        },

        {
            name: 'Status',
            selector: (row) => row.registration_status || row.status || 'Active',
            sortable: true,
            width: '120px',
        },
    ];
    const optionsColumn = {
        name: 'Options',
        cell: (row) => (
            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.5rem' }}>
                {!row.active ? (
                    <Button variant="primary" size="sm" style={{ height: "30px" }} onClick={() => handleActivate(row)} disabled={isLoading || isSubmitting}>
                        Re-activate
                    </Button>
                ) : row.registration_status.includes('Approved') ? (
                    <>
                        <Button variant="danger" size="sm" style={{ height: "30px" }} onClick={() => handleDelete(row)} disabled={isLoading || isSubmitting}>
                            <Trash />
                        </Button>
                        {row.registered_user_id && (
                            <Button variant="warning" size="sm" style={{ height: "30px" }} onClick={() => handleReset(row)} disabled={isLoading || isSubmitting}>
                                <ArrowClockwise />
                            </Button>
                        )}
                    </>
                ) : row.registration_status === 'Submitted' ? (
                    <>
                        <Button variant="success" size="sm" style={{ height: "30px" }} onClick={() => handleApprove(row)} disabled={isLoading || isSubmitting}>
                            Approve
                        </Button>
                        <Button variant="danger" size="sm" style={{ height: "30px" }} onClick={() => handleReject(row)} disabled={isLoading || isSubmitting}>
                            Reject
                        </Button>
                    </>
                ) : null}
            </div>
        ),
        ignoreRowClick: true,
        width: '200px',
    };
    const columns = activeTab === 'rejected' ? baseColumns : [...baseColumns, optionsColumn];
    const handlePageChange = (page) => {
        setPageNo(page);
        setSelectedRows([]);
    };
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setPageNo(page);
        setSelectedRows([]);
    };
    const handleTabClick = (tab) => {
        setActiveTab(tab);
        setPageNo(1);
        setPerPage(5);
        setSelectedRows([]);
        setUsers([]);
        setTotalRows(0);
    };
    useEffect(() => {
        if (pageNo && perPage) { // selectedCountry &&
            fetchUsers();
        }
    }, [pageNo, perPage, activeTab, selectedRole]);
    const handleRowSelected = ({ selectedRows }) => {
        setSelectedRows(selectedRows);
    };
    const customStyles = {
        tableWrapper: {
            style: {
                overflowX: 'auto',
            },
        },
        table: {
            style: {
                border: '1px solid #dee2e6',
                minWidth: '1200px',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                padding: '10px 15px!important',
                borderRight: '1px solid #dee2e6',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box',
                '&:last-child': {
                    borderRight: 'none',
                },
            },
        },
        cells: {
            style: {
                padding: '5px 15px',
                borderRight: '1px solid #dee2e6',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box',
                '&:last-child': {
                    borderRight: 'none',
                },
            },
        },
        rows: {
            style: {
                '&:hover': {
                    backgroundColor: '#f1f1f1',
                },
            },
        },
        pagination: {
            style: {
                padding: '4px 8px',
                minHeight: '40px',
                border: 'none',
                padding: '5px 5px 5px 5px',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                backgroundColor: '#e8f5e9',
            },
            pageButtonsStyle: {
                backgroundColor: 'transparent',
                border: 'none',
                color: '#000',
                padding: '0.25rem 0.5rem',
                margin: '0 0.25rem',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: '#d4edda',
                },
                '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                },
            },
        },
    };
    return (
        <>
            <div className="wrapper pt-100px one">
                <div className="container-fluid">
                    <div className="row pt-3">
                        <div className="col-sm-12 col-md-12 col-lg-12">
                            <div className="box-config">
                                <h5>User Management</h5>
                                {isSuperAdmin && (
                                    <div className="row mb-3">
                                        <div className="col-sm-12 col-md-5 col-lg-5">
                                            <div className="row">
                                                <div className="col-sm-12 col-md-6 col-lg-6">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="country">Country <span className="text-danger">*</span></label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedCountry || ''}
                                                            onChange={onCountryChange}
                                                        >
                                                            <option value="">Select</option>
                                                            {countries &&
                                                                countries.map((country) => (
                                                                    <option key={country.country_id} value={country.country_id}>
                                                                        {country.country || country.name}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-6 col-lg-6">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="role">Role</label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedRole || ''}
                                                            onChange={onRoleChange}
                                                        >
                                                            {roles.map((role) => (
                                                                <option key={role.role_id} value={role.role_id}>
                                                                    {role.role}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12 col-md-12 col-lg">
                            <div className="filter-box border p-2" style={{ borderRight: '0px', height: 'auto' }}>
                                <h5 className="text-capitalize">Users</h5>
                                <div className="d-flex bg-light">
                                    <div
                                        className={`tab ${activeTab === 'submitted' ? 'active-tab' : ''}`}
                                        onClick={() => handleTabClick('submitted')}
                                        style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '14px', fontWeight: '500', borderBottom: activeTab === 'submitted' ? '2px solid #2e7d32' : 'none' }}
                                    >
                                        Submitted
                                    </div>
                                    <div
                                        className={`tab ${activeTab === 'approved' ? 'active-tab' : ''}`}
                                        onClick={() => handleTabClick('approved')}
                                        style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '14px', fontWeight: '500', borderBottom: activeTab === 'approved' ? '2px solid #2e7d32' : 'none' }}
                                    >
                                        Approved
                                    </div>
                                    <div
                                        className={`tab ${activeTab === 'rejected' ? 'active-tab' : ''}`}
                                        onClick={() => handleTabClick('rejected')}
                                        style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '14px', fontWeight: '500', borderBottom: activeTab === 'rejected' ? '2px solid #2e7d32' : 'none' }}
                                    >
                                        Rejected
                                    </div>
                                    <div
                                        className={`tab ${activeTab === 'deleted' ? 'active-tab' : ''}`}
                                        onClick={() => handleTabClick('deleted')}
                                        style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '14px', fontWeight: '500', borderBottom: activeTab === 'deleted' ? '2px solid #2e7d32' : 'none' }}
                                    >
                                        Deleted
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex align-items-center">
                                        {selectedRows.length > 0 && (activeTab === 'submitted' || activeTab === 'rejected') && (
                                            <p className="mb-0">
                                                {selectedRows.length} row(s) selected
                                            </p>
                                        )}
                                        {selectedRows.length > 0 && (activeTab === 'submitted' || activeTab === 'rejected') && (
                                            <div className="ms-3">
                                                <button
                                                    className="btn btn-success me-2"
                                                    onClick={handleBulkApprove}
                                                    disabled={isLoading || isSubmitting}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    className="btn btn-danger"
                                                    onClick={handleBulkReject}
                                                    disabled={isLoading || isSubmitting}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <form onSubmit={handleSearchSubmit} className="d-flex align-items-center">
                                            <div className="searchBox me-2">
                                                <input
                                                    type="text"
                                                    placeholder="Search by name or email or phone"
                                                    className="form-control"
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                    }}
                                                    disabled={isLoading || isSubmitting}
                                                />
                                                <span><img src="./assets/images/search.svg" alt="" /></span>
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-search"
                                                disabled={isLoading || isSubmitting}
                                            >
                                                Search
                                            </button>
                                        </form>
                                        <div>
                                            <button className="btn btn-new ms-2 pe-2" onClick={reset} style={{ marginBottom: '23px' }} disabled={isLoading || isSubmitting}>
                                                Reset
                                            </button>
                                        </div>
                                        {/* <div>
                                            <button className="btn btn-new ms-2 pe-2" onClick={handleShow} style={{ marginBottom: '23px' }} disabled={isLoading || isSubmitting}>
                                                <span><img src="./assets/images/add.svg" alt="" style={{ height: '21px' }} /></span>Add New
                                            </button>
                                        </div> */}
                                        {/* New Export Button */}
                                        <div>
                                            <button
                                                className="btn btn-new ms-2 pe-2"
                                                onClick={handleExport}
                                                style={{ marginBottom: '23px' }}
                                                disabled={isLoading || isSubmitting}
                                            >
                                                <span><img src="./assets/images/export.svg" alt="" style={{ height: '21px' }} /></span>Export Data
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {fetchError && <div className="text-danger text-center p-2">{fetchError}</div>}
                                <DataTable
                                    key={activeTab}
                                    columns={columns}
                                    data={users}
                                    selectableRows={activeTab === 'submitted'}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={pageNo}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                    paginationPerPage={perPage}
                                    paginationComponentOptions={{
                                        rowsPerPageText: 'Records per page:',
                                        rangeSeparatorText: 'of',
                                        selectAllRowsItem: false,
                                    }}
                                    customStyles={customStyles}
                                    noDataComponent={<div className="text-center p-2 font-14px">No Users found</div>}
                                    highlightOnHover
                                    dense
                                    keyField="fisher_id"
                                    progressPending={isLoading}
                                    progressComponent={<div className="text-center p-2 font-14px">Loading...</div>}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
}
export default RegisteredUsers;